/**
 * Défi STREAMÉ (31/10/2026) : l'élève se filme pendant un exercice ; la vidéo est publiée comme un défi avec la
 * CHRONOLOGIE de l'exercice (moment où chaque question apparaît, où il répond, où tombe la note). Au visionnage,
 * l'écran est partagé en deux : la vidéo en haut, l'exercice qui s'enchaîne en bas, au rythme de la vidéo.
 *
 * Les temps sont en millisecondes depuis le début de l'enregistrement. PUR (partagé app ⇄ site ⇄ edge function).
 */

export type StreamEvent =
  /** une question apparaît */
  | { t: number; kind: 'question'; block_id: string }
  /** l'élève répond (choix cochés, juste ou non) */
  | { t: number; kind: 'answer'; block_id: string; choice_ids: string[]; correct: boolean }
  /** la note de l'exercice */
  | { t: number; kind: 'grade'; correct: number; total: number };

/** Au plus 400 événements (une centaine de questions). */
export const STREAM_TIMELINE_MAX_EVENTS = 400;
/** Durée maximale d'un enregistrement : au-delà il s'arrête (fichier ≤ 50 Mo). */
export const STREAM_MAX_DURATION_MS = 10 * 60_000;
/** L'enregistrement s'arrête tout seul ce délai après la note. */
export const STREAM_STOP_AFTER_GRADE_MS = 5_000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isTime(t: unknown): t is number {
  return typeof t === 'number' && Number.isFinite(t) && t >= 0 && t <= 4 * 3_600_000;
}

/**
 * Lit une chronologie reçue (base, requête) : les événements valides, dans l'ordre du temps ; null si ce n'est
 * pas une liste ou si elle est trop longue. Un événement mal formé est écarté.
 */
export function parseStreamTimeline(raw: unknown): StreamEvent[] | null {
  if (!Array.isArray(raw) || raw.length > STREAM_TIMELINE_MAX_EVENTS) return null;
  const out: StreamEvent[] = [];
  for (const e of raw) {
    if (!e || typeof e !== 'object') continue;
    const o = e as Record<string, unknown>;
    if (!isTime(o.t)) continue;
    const t = Math.round(o.t);
    if (o.kind === 'question' && typeof o.block_id === 'string' && UUID.test(o.block_id))
      out.push({ t, kind: 'question', block_id: o.block_id });
    else if (
      o.kind === 'answer' &&
      typeof o.block_id === 'string' &&
      UUID.test(o.block_id) &&
      Array.isArray(o.choice_ids) &&
      o.choice_ids.length <= 12 &&
      o.choice_ids.every((c) => typeof c === 'string' && UUID.test(c)) &&
      typeof o.correct === 'boolean'
    )
      out.push({
        t,
        kind: 'answer',
        block_id: o.block_id,
        choice_ids: o.choice_ids as string[],
        correct: o.correct,
      });
    else if (
      o.kind === 'grade' &&
      Number.isInteger(o.correct) &&
      Number.isInteger(o.total) &&
      (o.total as number) >= 0 &&
      (o.correct as number) >= 0 &&
      (o.correct as number) <= (o.total as number)
    )
      out.push({ t, kind: 'grade', correct: o.correct as number, total: o.total as number });
  }
  return out.sort((a, b) => a.t - b.t);
}

export interface StreamFrame {
  /** la question affichée (null avant la première) */
  blockId: string | null;
  /** rang de la question (0, 1, …) ; -1 avant la première */
  index: number;
  /** nombre de questions de la chronologie */
  total: number;
  /** la réponse à cette question, si elle est déjà donnée à ce moment */
  answer: { choiceIds: string[]; correct: boolean } | null;
  /** la note, une fois tombée */
  grade: { correct: number; total: number } | null;
}

/** Ce que montre l'exercice au temps `t` de la vidéo (ms). PUR. */
export function streamFrameAt(timeline: readonly StreamEvent[], t: number): StreamFrame {
  const questions = timeline.filter((e) => e.kind === 'question');
  let blockId: string | null = null;
  let index = -1;
  let answer: StreamFrame['answer'] = null;
  let grade: StreamFrame['grade'] = null;
  for (const e of timeline) {
    if (e.t > t) break;
    if (e.kind === 'question') {
      blockId = e.block_id;
      index = questions.indexOf(e);
      answer = null;
    } else if (e.kind === 'answer' && e.block_id === blockId) {
      answer = { choiceIds: e.choice_ids, correct: e.correct };
    } else if (e.kind === 'grade') {
      grade = { correct: e.correct, total: e.total };
    }
  }
  return { blockId, index, total: questions.length, answer, grade };
}

/** Note sur 20 d'un exercice (bonnes réponses / questions), au demi-point. PUR. */
export function streamGradeOn20(correct: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((correct / total) * 40) / 2;
}
